import { Injectable, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { configurationSelectors, settingsSelectors, State, UserType } from '@wam/shared';
import { take, tap } from 'rxjs/operators';
import {
  addGlobalContexts,
  newTracker,
  trackPageView,
  trackSelfDescribingEvent,
} from '@snowplow/browser-tracker';
import { Toggles } from '@app/shared/models/toggles.enum';
import { combineLatest, filter } from 'rxjs';
import { authenticationSelectors } from '@wam/authentication';
import { ButtonClickTrackingPlugin } from '@snowplow/browser-plugin-button-click-tracking';
import { FormTrackingPlugin } from '@snowplow/browser-plugin-form-tracking';
import { SettingsService } from '@app/settings/settings.service';
import { ResourceAndActivitiesTrackingEvent } from '@app/shared/snowplow/resource-and-activities-tracking-event';
import { ResourceAndActivitiesSearchTrackingEvent } from '@app/shared/snowplow/resource-and-activities--search-tracking-event';
import { ResourceAndActivitiesQuickFilterTrackingEvent } from '@app/shared/snowplow/resource-and-activities-quick-filter-tracking-event';
import { ResourceAndActivitiesFilterTrackingEvent } from '@app/shared/snowplow/resource-and-activities-filter-tracking-event';
import { LinkClickTrackingPlugin } from '@snowplow/browser-plugin-link-click-tracking';

export enum EventType {
  CLICK = 'click',
  SEARCH = 'search',
}

export enum EventCategory {
  RESET_WACS_BENCHMARK = 'reset_wacs_benchmark',
  OPEN_REPORTS = 'open_reports',
  RESOURCE_AND_ACTIVITIES = 'resource_and_activities',
  RESOURCE_AND_ACTIVITIES_SEARCH = 'resource_and_activities_search',
  RESOURCE_AND_ACTIVITIES_QUICK_FILTER = 'resource_and_activities_quick_filter',
  RESOURCE_AND_ACTIVITIES_FILTER = 'resource_and_activities_filter',
  DASHBOARD_CARD_MENU = 'dashboard_card_menu',
  TERMINATE_SESSION = 'terminate_session',
  OVERRIDE_GOALS = 'override_goals',
}

@Injectable({
  providedIn: 'root',
})
export class SnowplowService {
  protected store = inject<Store<State>>(Store);
  private settingsService = inject(SettingsService);

  private userType = UserType.UNKNOWN;

  constructor() {
    this.store.pipe(select(settingsSelectors.getUserType)).subscribe((userType) => {
      this.userType = userType;
    });
  }

  initialize() {
    combineLatest([
      this.store.select(configurationSelectors.isEnabled(Toggles.SEND_SNOWPLOW_EVENTS)),
      this.store.select(authenticationSelectors.getCurrentUser),
    ])
      .pipe(
        filter(([snowPlowEnabled, user]) => snowPlowEnabled && !!user),
        take(1),
      )
      .subscribe(([snowPlowEnabled, user]) => {
        if (snowPlowEnabled) {
          const tracker = newTracker(
            'main-tracker',
            this.settingsService.settings.snowplow.collectorUrl,
            {
              appId: user.application,
              respectDoNotTrack: true,
              anonymousTracking: false,
              contexts: {
                session: true,
              },
              plugins: [
                ButtonClickTrackingPlugin(),
                FormTrackingPlugin(),
                LinkClickTrackingPlugin(),
              ],
              cookieSameSite: 'Lax',
            },
          );

          tracker?.setUserId(user.sub);

          addGlobalContexts([
            {
              schema: 'iglu:com.waterford/custom_global_events/jsonschema/1-0-0',
              data: {
                'X-Waterford-Client-ID': this.userType,
                'X-Waterford-Client-Version': this.settingsService.settings.version,
              },
            },
          ]);
        }
      });
  }

  trackPageView() {
    this.store
      .select(configurationSelectors.isEnabled(Toggles.SEND_SNOWPLOW_EVENTS))
      .pipe(
        take(1),
        tap((snowplowEnabled) => {
          if (snowplowEnabled) {
            trackPageView();
          }
        }),
      )
      .subscribe();
  }

  trackResourceAndActivitiesEvent(event: ResourceAndActivitiesTrackingEvent) {
    this.trackClickEvent(event.buttonId, EventCategory.RESOURCE_AND_ACTIVITIES, {
      document_title: event.documentTitle,
      document_id: event.documentId,
      resource_type: event.resourceType,
    });
  }

  trackResourceAndActivitiesSearchEvent(event: ResourceAndActivitiesSearchTrackingEvent) {
    this.trackEvent('search', EventType.SEARCH, EventCategory.RESOURCE_AND_ACTIVITIES_SEARCH, {
      search_string: event.searchString,
      neural_search: `${event.neuralSearch}`,
      total_count: event.totalCount,
    });
  }

  trackResourcesAndActivitiesQuickFilterEvent(
    event: ResourceAndActivitiesQuickFilterTrackingEvent,
  ) {
    this.trackClickEvent(
      `quick-filter-${event.quickFilterId}`,
      EventCategory.RESOURCE_AND_ACTIVITIES_QUICK_FILTER,
      {
        collection_name: event.collectionName,
      },
    );
  }

  trackResourceAndActivitiesFilterEvent(event: ResourceAndActivitiesFilterTrackingEvent) {
    const filterValue = Object.values(event.facets)
      .flat()
      .map((facet) => facet.value)
      .join(';');

    this.trackClickEvent('filter', EventCategory.RESOURCE_AND_ACTIVITIES_FILTER, {
      filter_value: filterValue,
    });
  }

  trackClickEvent(
    elementId: string,
    eventCategory: EventCategory,
    additionalData?: { [key: string]: string | number },
  ) {
    this.trackEvent(elementId, EventType.CLICK, eventCategory, additionalData);
  }

  trackEvent(
    elementId: string,
    eventType: EventType,
    eventCategory: EventCategory,
    additionalData?: { [key: string]: string | number },
  ) {
    combineLatest([
      this.store.select(configurationSelectors.isEnabled(Toggles.SEND_SNOWPLOW_EVENTS)),
      this.store.select(authenticationSelectors.getCurrentUser),
    ])
      .pipe(take(1))
      .subscribe(([snowPlowEnabled, user]) => {
        if (snowPlowEnabled) {
          trackSelfDescribingEvent({
            event: {
              schema: 'iglu:com.waterford/custom_generic_events/jsonschema/1-0-0',
              data: {
                element_id: elementId,
                event_type: eventType,
                event_category: eventCategory,
                event_payload: {
                  organization_id: user.organization,
                  user_id: user.sub,
                  user_roles: user.roles.join(','),
                  ...additionalData,
                },
              },
            },
          });
        }
      });
  }
}
